import React from 'react';
import { ArrowLeft, Trash2, Plus, Minus, ShoppingBag, Tag, ChevronRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { OFFICES } from '../data/database';
import { useI18n } from '../i18n';

const CartPage = ({ onBack, onNavigate }) => {
  const { cart, updateCartQty, removeFromCart, cartTotal, clearCart } = useApp();
  const { t } = useI18n();

  const groupedByBusiness = cart.reduce((acc, item) => {
    if (!acc[item.officeId]) {
      acc[item.officeId] = { items: [], business: OFFICES.find(b => b.id === item.officeId) };
    }
    acc[item.officeId].items.push(item);
    return acc;
  }, {});

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-100 sticky top-0 z-20 lg:top-[73px]">
          <div className="max-w-screen-2xl mx-auto flex items-center gap-4 px-6 py-4">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full lg:hidden">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{t('cart', 'cart')}</h1>
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center p-8 mt-20">
          <div className="w-24 h-24 lg:w-32 lg:h-32 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <ShoppingBag className="w-12 h-12 lg:w-16 lg:h-16 text-gray-400" />
          </div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">{t('cart', 'emptyCart')}</h2>
          <p className="text-gray-500 text-center mb-6 lg:text-lg">{t('cart', 'noProductsYet')}</p>
          <button 
            onClick={() => onNavigate('search-results')}
            className="bg-emerald-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-emerald-600"
          >
            {t('cart', 'exploreProducts')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32 lg:pb-8">
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20 lg:top-[73px]">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full lg:hidden">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{t('cart', 'cart')} ({cart.length})</h1>
          </div>
          <button onClick={clearCart} className="text-red-500 text-sm font-medium hover:text-red-600">
            {t('cart', 'clear')}
          </button>
        </div>
      </div>

      {/* Desktop Layout: Two Column */}
      <div className="max-w-screen-2xl mx-auto px-0 lg:px-6 py-4 lg:py-8">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {Object.entries(groupedByBusiness).map(([businessId, { items, business }]) => (
              <div key={businessId} className="bg-white rounded-none lg:rounded-2xl shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                  <button 
                    onClick={() => onNavigate('office', { id: parseInt(businessId) })}
                    className="font-medium text-gray-900 hover:text-emerald-600 flex items-center gap-2"
                  >
                    {business?.name || t('cart', 'store')}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {items.map((item) => (
                    <div key={item.id} className="p-4 lg:p-5 flex gap-4">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-20 h-20 lg:w-24 lg:h-24 object-cover rounded-xl"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate lg:text-lg">{item.name}</h3>
                        <p className="text-emerald-600 font-bold mt-1 lg:text-lg">€{item.price.toFixed(2)}</p>
                        
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-3 bg-gray-100 rounded-full">
                            <button 
                              onClick={() => updateCartQty(item.id, item.type, item.qty - 1)}
                              className="p-2 lg:p-2.5 hover:bg-gray-200 rounded-full transition-colors"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="font-medium w-6 text-center">{item.qty}</span>
                            <button 
                              onClick={() => updateCartQty(item.id, item.type, item.qty + 1)}
                              className="p-2 lg:p-2.5 hover:bg-gray-200 rounded-full transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <button 
                            onClick={() => removeFromCart(item.id, item.type)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Promo Code - Mobile */}
            <div className="bg-white rounded-none lg:rounded-2xl p-4 shadow-sm lg:hidden">
              <div className="flex items-center gap-3">
                <Tag className="w-5 h-5 text-gray-400" />
                <input 
                  type="text"
                  placeholder={t('cart', 'discountCode')}
                  className="flex-1 outline-none text-gray-800"
                />
                <button className="text-emerald-600 font-medium">{t('cart', 'apply')}</button>
              </div>
            </div>
          </div>

          {/* Order Summary - Desktop Sidebar */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-[130px]">
              <h3 className="text-lg font-bold text-gray-900 mb-6">{t('cart', 'orderSummary')}</h3>
              
              {/* Promo Code */}
              <div className="mb-6">
                <label className="text-sm text-gray-600 mb-2 block">{t('cart', 'discountCode')}</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3">
                    <Tag className="w-5 h-5 text-gray-400" />
                    <input 
                      type="text"
                      placeholder={t('cart', 'enterCode')}
                      className="flex-1 bg-transparent outline-none text-gray-800"
                    />
                  </div>
                  <button className="px-4 py-3 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 transition-colors">
                    {t('cart', 'apply')}
                  </button>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>{t('cart', 'subtotal')}</span>
                  <span>€{cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>{t('cart', 'shipping')}</span>
                  <span>€4,99</span>
                </div>
                <div className="h-px bg-gray-200 my-2" />
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>{t('cart', 'total')}</span>
                  <span>€{(cartTotal + 4.99).toFixed(2)}</span>
                </div>
              </div>

              <button 
                onClick={() => onNavigate('checkout')}
                className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors"
              >
                {t('cart', 'proceedToCheckout')}
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                {t('cart', 'freeShippingOver')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Summary - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 safe-area-bottom lg:hidden">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-gray-500 text-sm">{t('cart', 'total')}</p>
            <p className="text-2xl font-bold text-gray-900">€{cartTotal.toFixed(2)}</p>
          </div>
          <div className="text-right text-sm text-gray-500">
            <p>{t('cart', 'shipping')}: €4,99</p>
            <p className="font-medium text-gray-900">{t('cart', 'total')}: €{(cartTotal + 4.99).toFixed(2)}</p>
          </div>
        </div>
        <button 
          onClick={() => onNavigate('checkout')}
          className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors"
        >
          {t('cart', 'proceedToCheckout')}
        </button>
      </div>
    </div>
  );
};

export default CartPage;
